<script lang="ts" context="module">
	import { page } from "$app/stores";
	import type { Newsletter, Badge } from "$gql/generated";
	import { getCurrentUser } from "$lib/components/currentUser";
	import { Newsletters, setFolderPermissions } from "$lib/components/newsletters";
	import Page from "$lib/components/Page.svelte";
	import { formatNumber } from "$lib/components/utils/numberUtils";
	import { Badges } from "$lib/components/badges";
	import CopyToClipboard from "$lib/components/CopyToClipboard.svelte";
</script>

<script lang="ts">
	const currentUser = getCurrentUser();

	const { publicProfile, myNewslettersUrl, publicProfileUrl, isUserPreviewingOwnProfile, defaultFolder } = $page.data;
	const { newsletters: userNewsletters, badges, folderNewsletters, isInGroupManagedByCurrentUser, isAdminOrOwnerOfUser } = publicProfile;
	const newsletters = defaultFolder ? folderNewsletters : userNewsletters;

	$: myBadges = (badges as Badge[]).filter((b) => b.mine);
	$: notMyBadges = (badges as Badge[]).filter((b) => !b.mine);
	let showPrivateNewsletters = (isInGroupManagedByCurrentUser || isAdminOrOwnerOfUser) && !isUserPreviewingOwnProfile;

	$: currentlyDisplayedNewsletters = showPrivateNewsletters ? newsletters : newsletters.filter((n: Newsletter) => n.isPublic);

	$: newsletterList = groupNewsletters(currentlyDisplayedNewsletters);

	$: views = countViews(currentlyDisplayedNewsletters);

	setFolderPermissions({
		folderId: "public_newsletters",
		can_delete_newsletters: false,
		can_remove_newsletters: false,
		can_edit_newsletters: false,
		can_add_newsletters: false,
		can_delete_folder: false,
		can_edit_folder: false,
		can_update_permissions: false
	});

	function groupNewsletters(newsletterItems: Newsletter[]) {
		const groupsByName: Record<string, { name: string; items: Newsletter[] }> = {};

		// Formats the groups as: "[long month] [4-digit year]" (e.g. "September 2020")
		const dateFormatForGroupName = new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" });

		// Sorts the newsletter by their "last created" date.
		// This date is formatted in the server as ISO8601 (e.g. "2020-11-23T10:37:46+00:00") so no problem sorting it here.
		const sortedNewsletterItems = newsletterItems.sort((n1, n2) => {
			if (n1.timestamps.lastUpdated > n2.timestamps.lastUpdated) {
				return -1;
			}
			if (n1.timestamps.lastUpdated < n2.timestamps.lastUpdated) {
				return 1;
			}
			return 0;
		});

		sortedNewsletterItems.forEach((n) => {
			const lastSavedAtDate = new Date(n.timestamps.lastUpdated);
			const groupName = dateFormatForGroupName.format(lastSavedAtDate);

			if (!groupsByName[groupName]) {
				groupsByName[groupName] = {
					name: groupName,
					items: []
				};
			}

			groupsByName[groupName].items.push(n);
		});

		return Object.values(groupsByName);
	}

	function countViews(newsletterItems: Newsletter[]) {
		return newsletterItems.reduce((total, newsletterListItem) => total + newsletterListItem.metrics.views, 0);
	}
</script>

<Page border={{ color: "teal", opacity: 50 }}>
	{#if (isUserPreviewingOwnProfile || isAdminOrOwnerOfUser || isInGroupManagedByCurrentUser) && !defaultFolder}
		<div class="relative mb-8 rounded border bg-orange-100 p-4 text-center text-13 [&>h1]:text-base [&>h1]:font-medium">
			{#if isUserPreviewingOwnProfile}
				<h1>This is your public profile, only public newsletters are viewable to visitors.</h1>
				Remove newsletters from your public profile by making newsletters private.
				<a href="https://help.smore.com/article/1277-your-public-smore-profile" class="absolute right-0 top-0 hidden p-4 md:block"
					><h1 class="text-teal-600">Learn more</h1></a>
			{:else if isAdminOrOwnerOfUser}
				<h1>{publicProfile.name} is a member of {currentUser.organization?.name}.</h1>
				As an admin, you can see all of their public and private newsletters. However, visitors to their public profile will only see newsletters
				listed as private.
			{:else if isInGroupManagedByCurrentUser}
				<h1>{publicProfile.name} is a member of a group you manage.</h1>
				As an admin, you can see all of their public and private newsletters. However, visitors to their public profile will only see newsletters
				listed as private.
			{/if}
		</div>
	{/if}
	<div class="box-border flex flex-col items-start justify-start lg:flex-row">
		<div class="sidebar hidden lg:block">
			<img
				class="h-16 w-16 flex-shrink-0 rounded-full border bg-gray-100 bg-cover bg-center bg-no-repeat text-xs"
				src={publicProfile.urls.avatar}
				alt={`${publicProfile.name} profile picture`} />
			<div class="mt-3 text-[26px] font-bold">{publicProfile.name}</div>
			<div class="text-base text-brand-gray-70">{publicProfile.bio || ""}</div>
			<div class="mt-5 flex items-center justify-between">
				<div>
					<span class="material-icons block text-xl text-teal-600">email</span>
					<div class="mt-2 text-base">
						{formatNumber(currentlyDisplayedNewsletters.length)}
						{currentlyDisplayedNewsletters.length == 1 ? "newsletter" : "newsletters"}
					</div>
				</div>
				<div>
					<span class="material-icons block text-xl text-teal-600">visibility</span>
					<div class="mt-2 text-base">
						{formatNumber(views)}
						{views == 1 ? "view" : "total views"}
					</div>
				</div>
			</div>
			<!-- <div class="py-2 mt-5 text-sm text-center text-white bg-blue-600 rounded sm-clickable hover:bg-blue-500">Contact {publicProfile.name}</div> -->
			<div class="mt-8 w-full">
				<Badges hideDescription {myBadges} {notMyBadges} />
			</div>
		</div>
		<div class="newsletters-container ml-0 flex w-full flex-col items-start justify-center lg:ml-16">
			<div class="flex w-full items-center justify-between">
				<p class="text-lg font-bold">
					{#if defaultFolder}{defaultFolder.name}{:else}{publicProfile.name}'s Newsletters{/if}
					<span class="text-base text-brand-gray-70">({formatNumber(currentlyDisplayedNewsletters.length)})</span>
				</p>
				{#if isUserPreviewingOwnProfile}
					<div class="ml-4 flex items-center justify-start">
						<a href={myNewslettersUrl} class="sm-clickable mr-6 shrink-0 text-sm font-normal text-brand-gray">Back to Newsletters</a>
						<div
							class="sm-clickable shrink-0 truncate rounded border border-blue-600 bg-blue-600 px-3 py-2 text-14 font-medium text-white shadow hover:border-blue-500 hover:bg-blue-500">
							<div class="flex items-center">
								<CopyToClipboard textToCopy={publicProfileUrl}>
									<span class="flex items-center font-medium text-white">
										<i class="material-icons md-18 mr-2 opacity-50">file_copy</i> Copy Profile Link
									</span>
								</CopyToClipboard>
							</div>
						</div>
					</div>
				{/if}
			</div>
			<div class="mt-2 w-full">
				{#each newsletterList as group (group.name)}
					<div class="flex w-full items-center justify-between">
						<h4 class="mb-2 mt-2 text-md font-light text-brand-gray-70">{group.name}</h4>
					</div>
					<div class="mt-2 w-full">
						<Newsletters newsletters={group.items} hideOverlay />
					</div>
				{:else}No newsletters found{/each}
			</div>
		</div>
	</div>
	<div></div>
</Page>
